<template>
    <div class="carrier-service-checkbox-field">
        <b-tabs v-if="visibleCarriers.length > 0" small pills>
            <b-tab v-for="carrier in visibleCarriers"
                   :key="carrier"
                   :title-link-class="['pl-2', 'pr-2', isDisabled(carrier) ? 'text-muted' : '']">
                <template #title>
                    <i v-if="isDisabled(carrier)" class="fa fa-ban mr-1" />
                    {{ carrier }}
                    <b-badge v-if="selectedCount(carrier) > 0"
                             pill
                             :variant="isDisabled(carrier) ? 'secondary' : 'primary'"
                             class="ml-1">
                        {{ selectedCount(carrier) }}
                    </b-badge>
                </template>
                <div class="pt-3">
                    <b-alert v-if="isDisabled(carrier)" show variant="warning" class="small mb-3">
                        <i class="fa fa-info-circle mr-1" />
                        <strong>{{ carrier }}</strong> is not in
                        <em>Allowed Carriers</em>. Re-enable the carrier above to make these
                        options selectable. Existing selections are preserved but ignored at
                        label-creation time.
                    </b-alert>
                    <b-form-checkbox-group
                        :checked="checkedFor(carrier)"
                        :options="optionsFor(carrier)"
                        :disabled="isDisabled(carrier)"
                        stacked
                        @input="onCarrierInput(carrier, $event)"
                    />
                </div>
            </b-tab>
        </b-tabs>
        <p v-else class="text-muted small mb-0">
            No carrier service codes are catalogued yet. Populate
            <code>ShippingServiceCatalog</code> to expose options here.
        </p>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

/**
 * Multi-carrier checkbox editor for tenant-wide allow-lists. Storage stays a flat
 * <c>string[]</c> of service codes (codes are unique across carriers — FedEx UPPER_SNAKE
 * vs USPS lowercase snake_case — so no prefix needed). The UI groups them under per-
 * carrier tabs so adding USPS/UPS catalogs later doesn't bloat one giant scrollable list.
 */
@Component({ name: "CarrierServiceCheckboxField" })
export default class CarrierServiceCheckboxField extends Vue {
    @Prop({ default: () => [] }) value!: string[];

    /** Carrier name → list of service codes that belong to that carrier. */
    @Prop({ type: Object, required: true }) groups!: Record<string, readonly string[]>;

    /**
     * Group keys (carrier names) whose checkboxes should be rendered as disabled because
     * the carrier itself isn't permitted at a higher level (Allowed Carriers field). Cascades
     * the carrier-level disable down to per-service controls so admins can't pick services
     * from a forbidden carrier.
     */
    @Prop({ type: Array, default: () => [] }) disabledGroups!: string[];

    /** Only render tabs for carriers that have at least one option. */
    get visibleCarriers(): string[] {
        return Object.keys(this.groups).filter(c => (this.groups[c]?.length ?? 0) > 0);
    }

    optionsFor(carrier: string): Array<{ text: string; value: string }> {
        return (this.groups[carrier] ?? []).map(s => ({ text: s, value: s }));
    }

    isDisabled(carrier: string): boolean {
        return (this.disabledGroups ?? []).includes(carrier);
    }

    /**
     * Subset of <c>value</c> that belongs to this carrier — drives which boxes are checked.
     * Backwards-compat: when the global value is null/empty (the data-model default for "all
     * allowed"), every box on a permitted carrier renders as checked so the UI matches the
     * backend semantic. Disabled carriers (forbidden by the Allowed Carriers field) render
     * unchecked-by-default so the cascade is visually clear. Toggling a box transitions the
     * parent value from null to an explicit allow-list.
     */
    checkedFor(carrier: string): string[] {
        const carrierCodes = this.groups[carrier] ?? [];
        const valueArr = Array.isArray(this.value) ? this.value : [];
        if (valueArr.length === 0) {
            return this.isDisabled(carrier) ? [] : [...carrierCodes];
        }
        const carrierCodeSet = new Set(carrierCodes);
        return valueArr.filter(v => carrierCodeSet.has(v));
    }

    selectedCount(carrier: string): number {
        return this.checkedFor(carrier).length;
    }

    /**
     * When the user toggles boxes in one tab, splice the new selection into the global value:
     * keep everything from OTHER carriers untouched, replace this carrier's slice with what
     * the checkbox-group emitted.
     */
    onCarrierInput(carrier: string, carrierSelection: string[]) {
        const carrierCodes = new Set(this.groups[carrier] ?? []);
        const valueArr = Array.isArray(this.value) ? this.value : [];
        const otherCarriers = valueArr.length === 0
            ? this.visibleCarriers
                .filter(c => c !== carrier && !this.isDisabled(c))
                .flatMap(c => this.groups[c] ?? [])
            : valueArr.filter(v => !carrierCodes.has(v));
        this.$emit("input", [...otherCarriers, ...carrierSelection]);
    }
}
</script>

<style scoped>
.carrier-service-checkbox-field >>> .nav-pills .nav-link {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
}
</style>
